/* Legends pre-orders: ship together on the latest date. Pure planning logic, no DOM and
   no network. Loaded before legends-cart-drawer.js, which runs the operations.

   Shopify charges postage per delivery group and groups cart lines by the ship date on
   their selling plan, so a cart holding a launch-dated frame and a November-dated frame
   paid postage twice (#LOE-2127, #LOE-2141 on 2026-09-15/16). James's rule, 2026-09-16:
   such a cart ships as ONE order on the LATEST date in it. This module decides which lines
   to move and where; it never blocks, never removes a line, never opens a modal.

   The plans and their dates come from STOQ's own page config
   (window._RestockRocketConfig.sellingPlans), which is present on every page type, so
   nothing here has to be maintained when an offer's date moves or a new offer is added.

   Stateless on purpose: every run recomputes the latest date from the lines' NATIVE plans
   (the first enabled offer holding the variant, STOQ's own rule), so when the latest-dated
   line leaves the cart the next run moves everything back to its own date. */
(function (window) {
  'use strict';

  var GIFT_PROP = '_free_case';
  /* The line property STOQ stamps at add time with the phrase after "Shipping:". It is
     also written onto the order, so a re-dated line must carry the new phrase or the order
     record and any reminder email would name the old date. Key is the same in every
     locale; only the value is localised (checked on 40 orders across 15 locales). */
  var SHIP_PROP = 'Shipping';
  /* Our own hidden marker, written onto a line that had NO selling plan and that this
     planner put onto a pre-order date. Two things need it and neither can be worked out
     from the cart alone.

     Moving BACK. The module's stateless rule is that when the latest-dated line leaves the
     cart everything returns to its own date. A line the customer bought in stock has no
     date to return to, and once we have given it a plan it is indistinguishable from one
     they pre-ordered on purpose: STOQ attaches the in-stock variants to the offer too, so
     even its native plan says November. The marker is the only record that the customer
     chose "now", and it is what lets the plan be taken off again.

     SAYING SO. The drawer shows its "ships together" line only when something was actually
     re-dated. A dated line that moved is recognisable for ever after because its native
     plan still differs from the one it is on; a line we moved off no plan at all is not,
     so without the marker the notice would appear once and vanish on the next cart read,
     on exactly the cart that most needs it.

     Underscore-prefixed, so Shopify keeps it out of the cart, the checkout and the order
     the customer sees. */
  var TOGETHER_PROP = '_ships_together';
  var TOGETHER_MARK = '1';
  /* Our hidden marker for a CARRIED line: one moved onto a plan that is not the one whose
     date it now wears (see THE CARRIER in computeShipTogetherOps). The value is the id of
     the plan the date came from. Underscore-prefixed for the same reason as the one above. */
  var DATED_BY_PROP = '_ship_date_from';
  var DATE_TOKEN = /\{\{\s*date\s*\}\}/g;

  function lineVariantId(item) {
    if (item.variant_id != null) return item.variant_id;
    if (item.id != null) return item.id;
    return null;
  }
  function linePlanId(item) {
    var alloc = item.selling_plan_allocation;
    if (!alloc || !alloc.selling_plan) return null;
    var id = alloc.selling_plan.id;
    return id == null ? null : id;
  }

  /* Normalise STOQ's config into what the planner needs. Keeps STOQ's order, because
     "first plan holding the variant" is how STOQ decides a variant's own offer. */
  function plansFromStoq(rr) {
    if (!rr || !Array.isArray(rr.sellingPlans)) return [];
    var disabled = {};
    var i, p, t, vids, k;
    var dis = Array.isArray(rr.disabledSellingPlanIds) ? rr.disabledSellingPlanIds : [];
    for (i = 0; i < dis.length; i++) disabled[String(dis[i])] = true;
    var out = [];
    for (i = 0; i < rr.sellingPlans.length; i++) {
      p = rr.sellingPlans[i];
      if (!p || !p.enabled || p.shopify_selling_plan_id == null) continue;
      if (disabled[String(p.shopify_selling_plan_id)]) continue;
      t = Date.parse(p.delivery_at);
      if (isNaN(t)) continue;
      vids = {};
      var list = Array.isArray(p.variant_ids) ? p.variant_ids : [];
      for (k = 0; k < list.length; k++) vids[String(list[k])] = true;
      out.push({
        id: p.shopify_selling_plan_id,
        deliveryAt: t,
        text: typeof p.preorder_shipping_text === 'string' ? p.preorder_shipping_text : '',
        variantIds: vids
      });
    }
    return out;
  }

  function nativePlan(variantId, plans) {
    if (variantId == null || !plans) return null;
    var key = String(variantId);
    for (var i = 0; i < plans.length; i++) {
      if (plans[i].variantIds[key]) return plans[i];
    }
    return null;
  }

  function formatDate(ms, locale) {
    var d = new Date(ms);
    try {
      return d.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return d.toDateString();
    }
  }
  /* The whole phrase, as STOQ shows it on the product page ("Shipping: early November",
     "Shipping: Sep 18, 2026"). Renders the token in the visitor's timezone exactly as STOQ
     does, so the drawer and the product page agree. */
  function shippingText(plan, locale) {
    return String(plan.text || '').replace(DATE_TOKEN, formatDate(plan.deliveryAt, locale));
  }
  /* What STOQ writes into the Shipping line property: the phrase after the label. */
  function shippingValue(plan, locale) {
    var full = shippingText(plan, locale);
    var i = full.indexOf(':');
    return (i === -1 ? full : full.slice(i + 1)).replace(/^\s+|\s+$/g, '');
  }

  function copyProps(props) {
    var out = {}, k;
    for (k in props) {
      if (Object.prototype.hasOwnProperty.call(props, k)) out[k] = props[k];
    }
    return out;
  }

  /* The properties a line goes back to when its plan is taken off again. Leaving the
     Shipping stamp behind would print a November date on a line that ships now, and leaving
     the marker behind would keep the drawer claiming a shared date the cart no longer has.

     BLANKED, not deleted, because Shopify will not delete one. Measured against the live
     cart API on 2026-09-22: POST /cart/change.js with `properties: {}` is read as "leave
     the properties alone" and the line comes back with all of them intact, and passing a
     key as "" or null keeps the key with that value rather than dropping it. Blank is
     enough for every reader that matters. Shopify hides a blank line property from the
     cart, the checkout and the order; stampFor already refuses a blank as a stamp; and a
     blank marker is falsy, so the line reads as what it is again, a line with no date of
     its own. Only keys the line actually carried are blanked, so nothing gains an empty
     property it never had. */
  function unstampProps(props) {
    var out = copyProps(props || {});
    if (Object.prototype.hasOwnProperty.call(out, SHIP_PROP)) out[SHIP_PROP] = '';
    if (Object.prototype.hasOwnProperty.call(out, TOGETHER_PROP)) out[TOGETHER_PROP] = '';
    if (Object.prototype.hasOwnProperty.call(out, DATED_BY_PROP)) out[DATED_BY_PROP] = '';
    return out;
  }

  /* The Shipping value to stamp on a line moving onto `plan`.

     Shopify hashes a line's properties into its line key, so a line we move onto a plan
     and a line STOQ itself added to that plan only merge into one line if their Shipping
     strings match character for character. Ours would not: toLocaleDateString renders the
     launch date as "18 Sept 2026" on en-GB where STOQ stamps "Sep 18, 2026", and the
     customer would be left looking at the same frame twice. Whatever is already sitting on
     that plan in this cart IS STOQ's own stamp, so copy it verbatim and only compute a
     value when the cart holds nothing on that plan yet. First such line wins; a blank
     property is not a stamp, and neither is a carried line's, which wears another plan's
     date. */
  function stampFor(plan, lines, locale) {
    var i, v, props;
    for (i = 0; i < lines.length; i++) {
      if (String(lines[i].current.id) !== String(plan.id)) continue;
      props = lines[i].item.properties || {};
      if (props[DATED_BY_PROP]) continue;
      v = props[SHIP_PROP];
      if (typeof v === 'string' && v.replace(/^\s+|\s+$/g, '') !== '') return v;
    }
    return shippingValue(plan, locale);
  }

  function holdsAll(plan, vids) {
    for (var i = 0; i < vids.length; i++) {
      if (vids[i] == null || !plan.variantIds[String(vids[i])]) return false;
    }
    return true;
  }

  /* The plan to carry the cart on when `latest` cannot hold every line: the latest-dated
     other plan that holds them all, ties broken on STOQ's order. Null when there is none,
     and the cart then splits exactly as it did before carrying existed. */
  function carrierFor(latest, plans, order, vids) {
    var best = null, p, i;
    for (i = 0; i < plans.length; i++) {
      p = plans[i];
      if (String(p.id) === String(latest.id) || !holdsAll(p, vids)) continue;
      if (!best || p.deliveryAt > best.deliveryAt ||
          (p.deliveryAt === best.deliveryAt && order[String(p.id)] < order[String(best.id)])) {
        best = p;
      }
    }
    return best;
  }

  /* The Shipping value every carried line wears: STOQ's own stamp off a line whose native
     plan IS latest (the flight case keeps the one STOQ gave it, wherever it now sits), so
     the carried lines match it character for character; computed only when none has one. */
  function carriedStamp(latest, dated, locale) {
    var i, v;
    for (i = 0; i < dated.length; i++) {
      if (String(dated[i].own.id) !== String(latest.id)) continue;
      v = (dated[i].item.properties || {})[SHIP_PROP];
      if (typeof v === 'string' && v.replace(/^\s+|\s+$/g, '') !== '') return v;
    }
    return shippingValue(latest, locale);
  }

  /* Whether a line has to be written to end up on `target`. Off it, always. On it, a
     carried line has to wear the carried date and marker, and any other line must not
     still carry a marker from a carry that no longer applies. */
  function needsMove(line, target, carrier, stamp, datedBy) {
    var props = line.item.properties || {};
    if (!line.current || String(line.current.id) !== String(target.id)) return true;
    if (carrier) return props[SHIP_PROP] !== stamp || String(props[DATED_BY_PROP] || '') !== datedBy;
    return !!props[DATED_BY_PROP];
  }

  /* A line's properties with a new Shipping stamp, marked as carried when datedBy is set,
     and with any old carry marker blanked when it is not (blanked, as unstampProps
     explains, because Shopify will not delete a property). */
  function restamp(props, ship, datedBy) {
    var out = copyProps(props || {});
    out[SHIP_PROP] = ship;
    if (datedBy) out[DATED_BY_PROP] = datedBy;
    else if (Object.prototype.hasOwnProperty.call(out, DATED_BY_PROP)) out[DATED_BY_PROP] = '';
    return out;
  }

  /* Returns the changes needed so the whole cart sits in ONE delivery group, on the latest
     pre-order date in it. Pure: no I/O, no mutation.

     Three kinds of line, and the difference matters:

       DATED       carries a plan we know and no marker. The customer chose that date. Only
                   these decide where the cart gathers: `latest` is the latest of their
                   NATIVE plans (the first enabled offer holding the variant, STOQ's own
                   rule), so the answer is the same on every run and a line moves back when
                   the line that dragged it leaves.
       JOINERS     carries no plan at all (bought in stock), or carries one and our marker,
                   which means we moved it last time. James, 2026-09-22: an in-stock item
                   and a pre-ordered one go out as ONE parcel on the pre-order date, so
                   these follow the dated lines rather than shipping now on their own
                   postage. With nothing dated in the cart they are put back.
       SKIPPED     carries a plan we do not know. Somebody else's subscription; not ours to
                   move, and never was.

     Gift lines are handled apart from the rest, in two halves. They never take part in
     computing `latest`: a gift twin left on the November offer after its frames went is a
     stale line, and letting it decide would drag the whole cart onto a date nothing in it
     is actually waiting for. But they DO have to follow the frames once `latest` is known.
     The gift planner compares only variant and quantity, so a gift line already holding the
     wanted twin makes it emit nothing: without the pass below the frames end on November
     and the gift sits elsewhere, which is two delivery groups and the second postage charge
     this whole feature exists to stop (#LOE-2127, #LOE-2141). Since 2026-09-22 a gift can
     also arrive with NO plan, because an in-stock AC/DC frame earns one, so the pass has to
     pick up plan-less gifts too.

     A line whose variant is not attached to the latest plan cannot take it (/cart/add and
     /cart/change 422 on an unattached plan), so it keeps what it has and `unified` comes
     back false: the drawer then makes no "ships together" claim. */
  function computeShipTogetherOps(cart, plans, opts) {
    var res = { ops: [], targetPlanId: null, datePlanId: null, unified: false, redated: 0, joined: 0 };
    if (!cart || !cart.items || !plans || !plans.length) return res;
    var locale = opts && opts.locale;
    var byId = {};
    var i, it, pid, current, vid, props, marked;
    for (i = 0; i < plans.length; i++) byId[String(plans[i].id)] = plans[i];

    var dated = [];
    var joiners = [];
    var giftLines = [];
    for (i = 0; i < cart.items.length; i++) {
      it = cart.items[i];
      if (!it) continue;
      pid = linePlanId(it);
      current = pid == null ? null : byId[String(pid)];
      // A plan we do not know is somebody else's. Leave it entirely alone, whatever else
      // the line carries.
      if (pid != null && !current) continue;
      vid = lineVariantId(it);
      props = it.properties || {};
      if (props[GIFT_PROP]) { giftLines.push({ item: it, vid: vid, current: current || null }); continue; }
      marked = !!props[TOGETHER_PROP];
      if (current && !marked) {
        dated.push({ item: it, vid: vid, current: current, own: nativePlan(vid, plans) || current });
      } else {
        joiners.push({ item: it, vid: vid, current: current || null, marked: marked });
      }
    }

    /* Nothing the customer dated: there is no date to gather on. Anything this planner
       moved here goes back to shipping now, and every gift line goes with it, because a
       gift always follows its frame and no frame is dated any more. A cart that only ever
       held in-stock lines produces nothing at all, which is the point: the drawer must not
       invent a pre-order out of a basket that has none. */
    if (!dated.length) {
      for (i = 0; i < joiners.length; i++) {
        if (!joiners[i].current) continue;
        res.ops.push({
          type: 'change',
          key: joiners[i].item.key,
          quantity: joiners[i].item.quantity,
          sellingPlanId: '',
          properties: unstampProps(joiners[i].item.properties)
        });
      }
      /* Gifts are put back only when there is something in the cart for them to belong to.
         A cart holding NOTHING but a gift line is a cart the gift planner is about to empty
         of it anyway, and the older rule of leaving that one entirely alone is kept: a
         write to clear a plan off a line that is about to be deleted is a write for
         nothing. */
      for (i = 0; joiners.length && i < giftLines.length; i++) {
        if (!giftLines[i].current) continue;
        res.ops.push({
          type: 'change',
          key: giftLines[i].item.key,
          quantity: giftLines[i].item.quantity,
          sellingPlanId: '',
          properties: unstampProps(giftLines[i].item.properties)
        });
      }
      return res;
    }

    /* The latest ship date among the dated lines' native plans. Two offers can share a
       date, so the tie is broken on STOQ's own order rather than on cart order: one cart
       must always resolve to the same plan however the customer happened to add the
       lines. */
    var order = {};
    for (i = 0; i < plans.length; i++) order[String(plans[i].id)] = i;
    var latest = dated[0].own;
    for (i = 1; i < dated.length; i++) {
      var o = dated[i].own;
      if (o.deliveryAt > latest.deliveryAt ||
          (o.deliveryAt === latest.deliveryAt && order[String(o.id)] < order[String(latest.id)])) {
        latest = o;
      }
    }
    /* THE CARRIER (James, 2026-10-23). The plan the lines move onto is normally `latest`
       itself, but an offer that holds only a few variants cannot take the rest of the cart:
       the flight case offer holds just the two cases, so a flight case and a pair of
       sunglasses could not share it and the order paid postage twice (#LOE-3059). Putting
       the whole shop on that offer is not an option either, because STOQ shows a variant
       its NEWEST offer and every product page would then read "late November".

       So when `latest` cannot hold every line, the lines are carried on another plan that
       can (the November offer holds every variant in the shop), and only the DATE comes
       from `latest`: every line is stamped with latest's Shipping value. Shopify groups
       delivery by plan and STOQ names every plan just "Preorder" at checkout, so the
       customer sees one parcel, one postage charge and "Shipping: late November" on every
       line, and the order says the same. Nothing here names an offer; it answers itself
       from STOQ's config, and a cart whose latest plan can hold everything never uses it.

       A carried line is marked with the plan whose date it wears (DATED_BY_PROP). That is
       what lets it go back: once the line that set the date leaves, the rest are already
       sitting on what is now their own plan, and without the marker the planner would see
       nothing to do and leave them stamped with a date nothing in the cart is waiting for. */
    var vids = [];
    for (i = 0; i < dated.length; i++) vids.push(dated[i].vid);
    for (i = 0; i < joiners.length; i++) vids.push(joiners[i].vid);
    for (i = 0; i < giftLines.length; i++) vids.push(giftLines[i].vid);
    var carrier = holdsAll(latest, vids) ? null : carrierFor(latest, plans, order, vids);
    var onto = carrier || latest;
    var stamp = carrier ? carriedStamp(latest, dated, locale) : null;
    var datedBy = carrier ? String(latest.id) : '';
    res.targetPlanId = onto.id;
    res.datePlanId = latest.id;

    var unified = true;
    for (i = 0; i < dated.length; i++) {
      var l = dated[i];
      var canJoin = l.vid != null && !!onto.variantIds[String(l.vid)];
      var target = canJoin ? onto : l.own;
      if (String(target.id) !== String(onto.id)) unified = false;
      // Re-dated means the date the customer will see moved, not the plan id: a carried
      // November frame stays on the November plan but now ships late November, and the
      // flight case that moved onto the carrier still ships on its own date.
      if (carrier ? String(l.own.id) !== String(latest.id) : String(target.id) !== String(l.own.id)) res.redated++;
      /* A line already sitting on its target plan is never restamped, on purpose: that is
         what keeps the planner idempotent, and it means an offer whose date STOQ later
         moves does not set off a restamp loop on every cart read. The exceptions are the
         carried lines, which must wear latest's date, and a line still marked from a carry
         that no longer applies. */
      if (!needsMove(l, target, carrier, stamp, datedBy)) continue;
      res.ops.push({
        type: 'change',
        key: l.item.key,
        quantity: l.item.quantity,
        sellingPlanId: target.id,
        properties: restamp(l.item.properties, carrier ? stamp : stampFor(target, dated, locale), datedBy)
      });
    }

    /* The in-stock lines join them. Each one counts as a re-dating for ever after, not
       just on the run that moved it, because the marker says the customer bought it to
       ship now: that is what keeps the drawer's notice on the screen rather than flashing
       up once and going. A line already on the target and already marked is left alone,
       which is what makes a second pass write nothing. */
    for (i = 0; i < joiners.length; i++) {
      var j = joiners[i];
      if (j.vid == null || !onto.variantIds[String(j.vid)]) { unified = false; continue; }
      res.redated++;
      /* Counted apart from `redated` because the drawer says something different about it.
         A cart that only ever mixed two pre-order DATES gets the old "ships together" line;
         a cart that mixes stock with a pre-order gets told plainly why it is waiting and
         what to take out to stop waiting. */
      res.joined++;
      if (j.marked && !needsMove(j, onto, carrier, stamp, datedBy)) continue;
      var jProps = restamp(j.item.properties, carrier ? stamp : stampFor(latest, dated, locale), datedBy);
      jProps[TOGETHER_PROP] = TOGETHER_MARK;
      res.ops.push({
        type: 'change',
        key: j.item.key,
        quantity: j.item.quantity,
        sellingPlanId: onto.id,
        properties: jProps
      });
    }

    /* The gift lines follow, AFTER every other op, so the gift twin lands on the date the
       frames have just been given rather than on one they are leaving. A gift keeps its
       properties, `_free_case` included: strip that and cart-item.liquid stops rendering
       the line as a locked gift and the customer is handed a quantity stepper on a line
       the planner owns. The Shipping stamp is harvested the same way a frame's is, off the
       dated lines already sitting on the target plan, so the value is STOQ's own string for
       that offer rather than one we formatted.

       A gift whose variant was never attached to the target offer cannot move, so it stays
       where it is and `unified` goes false: the cart really will ship in two groups and the
       drawer must not say otherwise. Gift lines are never counted in `redated` either. The
       notice is about dates the CUSTOMER chose, and a gift they never picked moving with
       its frames is not a cart that mixed dates. */
    for (i = 0; i < giftLines.length; i++) {
      var g = giftLines[i];
      if (!needsMove(g, onto, carrier, stamp, datedBy)) continue;
      if (g.vid == null || !onto.variantIds[String(g.vid)]) { unified = false; continue; }
      res.ops.push({
        type: 'change',
        key: g.item.key,
        quantity: g.item.quantity,
        sellingPlanId: onto.id,
        properties: restamp(g.item.properties, carrier ? stamp : stampFor(latest, dated, locale), datedBy)
      });
    }
    res.unified = unified;
    return res;
  }

  window.LegendsShipTogether = {
    SHIP_PROP: SHIP_PROP,
    TOGETHER_PROP: TOGETHER_PROP,
    TOGETHER_MARK: TOGETHER_MARK,
    DATED_BY_PROP: DATED_BY_PROP,
    plansFromStoq: plansFromStoq,
    nativePlan: nativePlan,
    shippingText: shippingText,
    shippingValue: shippingValue,
    computeShipTogetherOps: computeShipTogetherOps
  };
})(typeof window !== 'undefined' ? window : this);
